import { DeliveryType } from '../enums/DeliveryType';
import { FulfillmentStatus, FulfillmentType } from '../enums/Fulfillment';
import { OrderChannel, OrderConfirmationStatus, OrderEmailStatus, OrderStatus } from '../enums/OrderStatus';
import { RefundDestinationType } from '../enums/RefundDestinationType';
import { Buyer } from './Buyer';
import { CartSummary, OrderPaymentClearing } from './CartSummary';
import { DeliveryAddress } from './Delivery';
import { MovieShow } from './MovieShow';
import { PaymentMethod } from './PaymentMethod';
import { Shop } from './Shop';

export interface CartHoldBlocker {
  kind: string;
  field?: string;
  message: string;
}

export interface CartHoldBlockers {
  can_hold: boolean;
  blockers: CartHoldBlocker[];
}

export interface OrderDto {
  id: string;
  cart_id: string | null;
  number: string;
  shop_id: string;
  user_id: string;
  inserted: string;
  status: OrderStatus;
  user_status: string;
  context: string;
  /**
   * **Null on `GET /v1.0/orders`** (PAUL-3564). It was the biggest single block in a list response -
   * 65 KB of a 508 KB 20-order page, a 105-property object of which ~89 were null, repeated on every
   * row - and nothing rendered it there, so the list projection no longer selects it at all (which also
   * drops the `shops` join from the query). `GET /v1.0/orders/{id}` still carries it in full, so a
   * component that needs the shop must read the order, not the row.
   */
  shop: Shop | null;
  buyer: Buyer;
  /** Null on `GET /v1.0/orders` for the same reason as `shop` (PAUL-3564). */
  delivery: DeliveryAddress | null;
  delivery_method: DeliveryType;
  payment_url: string /* TODO: change to multiple orders model based on domain, this is used in studytrail, survey (some shared components) */;
  summary: CartSummary;
  account_exists: boolean;
  refunds: { items: OrderRefundInfo[] };
  // PAUL-2852: true when any refund leg of the order is currently failed (display-only, list + single).
  has_failed_refund?: boolean;
  // PAUL-3713: set when Cineville refused a reservation after payment. The order stays `paid` but has no
  // tickets; absent (not null) on every other order, list + single.
  fulfillment_failed_at?: string;
  // The error kind behind it, e.g. `cineville_max_active_reservations_reached`; unknown kinds are generic.
  fulfillment_failed_reason?: string;
  timeslot: string;
  pay_after: boolean;
  timeslot_interval: number;
  comment: string;
  paid_at: string;
  warning: string;
  refund_status: string;
  channel: OrderChannel;
  refundable: RefundCapabilities;
  total_refundable: number;
  pos_name: string;
  payment_methods: PaymentMethod[];
  hold_blockers?: CartHoldBlockers;
  confirmation_status?: OrderConfirmationStatus;
  external_email_id?: number | null;
  external_email_status?: OrderEmailStatus | null;
  external_email_sent?: string | null;
  fulfillment_status?: FulfillmentStatus;
  fulfillment_type?: FulfillmentType;
  fulfillment_served_at?: string;
  fulfillment_served_at_pos_name?: string;
  fulfillment_served_by_user_display_name?: string;
  // PAUL-3694: the cart's hold after the order extended it; null when the cart never expires. Absent on `GET /v1.0/orders`.
  reserved_until?: string | null;
  // Server clock when the response was built — count down `reserved_until - server_time`, not the device clock.
  server_time?: string;
}

export interface Order extends OrderDto {
  isSelected?: boolean;
  isExpanded?: boolean;
  detailsLoaded?: boolean;
  shows: MovieShow[];
}

export interface OrderRefundInfo {
  id: string;
  by_user_display_name: string;
  refunded_at: string;
  refund_status: string;
  reason: string | null;
  refund_payments?: OrderRefundPayment[];
  extra_costs?: OrderRefundExtraCost[];
}

export interface OrderRefundExtraCost {
  extra_cost_id: string;
  product_name: string;
  price_name: string;
  amount: number;
}

export type OrderRefundPaymentStatus =
  | 'pending'
  | 'processing'
  | 'queued'
  | 'completed'
  | 'failed'
  // PAUL-2852: operator-cancelled leg — stays listed, retryable / convertible while latest.
  | 'canceled';

export interface OrderRefundPayment {
  id: string;
  order_payment_id: string | null;
  payment_method_code: string;
  amount: number;
  status: OrderRefundPaymentStatus;
  failure_reason?: string | null;
  gift_card_id: string | null;
  // PAUL-2852: code of the gift card this leg issued/reloaded — shown in the Account column so
  // operators can find the card. Null when the leg has no gift card.
  gift_card_code?: string | null;
  inserted: string;
  // Archive marker: when set, this row was replaced and the id points at the row that carries the
  // payment now — the MAIN payment row for retry attempt-history snapshots (which also have
  // order_payment_id null), or the gift-card row after a convert-to-gift-card. Archived rows keep
  // their real terminal status (failed/canceled); resolve transitively to group history under the
  // live row.
  recovered_by_refund_payment_id?: string | null;
  clearing: OrderPaymentClearing | null;
  // PAUL-2852: the destination this leg routed to; null on legacy rows.
  destination_type?: RefundDestinationType | null;
  // PAUL-2852: cash-rounding delta on a Cash allocation; null otherwise.
  rounding_difference?: number | null;
  // PAUL-2852: G/L snapshot taken at finalize.
  general_ledger_account_id?: string | null;
  general_ledger_account_code?: string | null;
  order_payment?: {
    payment_method_name: string;
    amount: number;
    payer_account_name: string | null;
    payer_account_masked: string | null;
  } | null;
}

export enum RefundCapabilities {
  Full = 0,
  RequiredSelection = 1,
  Forbidden = 2,
}

export interface ResendOrderConfirmationResult {
  email: string;
  error: string | null;
  error_field: string | null;
  is_valid: boolean;
}
